import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal, flushSync } from "react-dom";
import { Turnstile } from "../../client/components/Turnstile";
import {
  CAPTURE_FIELD_ORDER,
  fieldErrorCopy,
  formErrorCopy,
  type CaptureFailure,
  type CaptureField,
  type CaptureSuccess,
} from "../../shared/prompts/capture-contract";
import { fillTemplate, getTemplate, templatesInCategory } from "../../shared/prompts/templates";
import { withBase } from "../lib/base-path";
import { copyText } from "../lib/clipboard";
import { prefersReducedMotion } from "../lib/dom";
import { captureBody, checkEmailField, checkGate } from "../lib/gate";
import { pushPbEvent, sha256EmailHex } from "../lib/gtm-events";
import { campaignQueryString } from "../../shared/leadgen/attribution";
import { normaliseEmail } from "../../shared/leadgen/work-email";
import {
  currentTemplate,
  fillInputsFor,
  isBusinessType,
  slotValueOf,
  type BuilderInputs,
  type BuilderSeed,
  type BuilderState,
  type BuilderStore,
  type GateFeedback,
  RESEND_WAIT_SECONDS,
} from "../lib/store";
import {
  BuilderView,
  CopyBar,
  type BuilderActions,
  type CopyStatus,
  type CopySurface,
} from "./BuilderView";

const BUILT_DEBOUNCE_MS = 1500;
const COPIED_RESET_MS = 1800;

function promptParams(inputs: BuilderInputs): Record<string, unknown> {
  return {
    prompt_id: currentTemplate(inputs).id,
    goal: inputs.goal,
    platform: inputs.platform,
    business_type: inputs.businessType || undefined,
  };
}

// Hosts whose Turnstile sitekey is live. Elsewhere the Worker skips verification
// (src/server/security/turnstile.ts ENFORCED_HOSTS), so the widget is not shown.
const TURNSTILE_ENFORCED_HOSTS = new Set([
  "staging.trafficsnapshot.skedsocial.com",
  "trafficsnapshot.skedsocial.com",
  "skedsocial.com",
]);
// Cloudflare's always-pass sitekey, paired server-side with the test secret by ?turnstile=test.
const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";

interface TurnstileConfig {
  show: boolean;
  siteKey: string | null;
  captureUrl: string;
}

function resolveTurnstile(siteKey: string, appEnv: string): TurnstileConfig {
  const search = new URLSearchParams(window.location.search);
  const testMode = appEnv !== "production" && search.get("turnstile") === "test";
  // The page's UTMs and click IDs ride along so the capture records which ad a lead came from.
  const query = [testMode ? "turnstile=test" : "", campaignQueryString((key) => search.get(key))]
    .filter(Boolean)
    .join("&");
  return {
    show: testMode || TURNSTILE_ENFORCED_HOSTS.has(window.location.hostname),
    siteKey: testMode ? TURNSTILE_TEST_SITE_KEY : siteKey || null,
    captureUrl: withBase(`/api/capture${query ? `?${query}` : ""}`),
  };
}

function withoutError(
  errors: Partial<Record<CaptureField, string>>,
  field: CaptureField,
): Partial<Record<CaptureField, string>> {
  const next = { ...errors };
  delete next[field];
  return next;
}

const FIELD_INPUT_IDS: Record<CaptureField, string> = {
  email: "g-email",
  role: "g-role",
  businessType: "g-biz",
  accounts: "g-accounts",
};

function focusFirstError(errors: Partial<Record<CaptureField, string>>): void {
  const first = CAPTURE_FIELD_ORDER.find((field) => errors[field]);
  if (!first) return;
  // After React commits the error state, so the described-by text exists when focus lands.
  window.setTimeout(() => document.getElementById(FIELD_INPUT_IDS[first])?.focus(), 0);
}

export function BuilderIsland({
  store,
  seed,
  turnstileSiteKey,
  appEnv,
}: {
  store: BuilderStore;
  seed: BuilderSeed;
  turnstileSiteKey: string;
  appEnv: string;
}) {
  const state = useSyncExternalStore(store.subscribe, store.get, store.get);
  const [copyStatus, setCopyStatus] = useState<CopyStatus>("idle");
  const [copybarVisible, setCopybarVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const builtTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const committed = useRef<Record<string, string>>({});
  const wasGateOpen = useRef(state.gateOpen);
  const turnstileConfig = useMemo(
    () => resolveTurnstile(turnstileSiteKey, appEnv),
    [turnstileSiteKey, appEnv],
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  // Ticks once a second while the resend wait runs, then stops.
  const [now, setNow] = useState(() => Date.now());
  const sentAt = state.feedback.sentAt;
  useEffect(() => {
    if (sentAt === null) return;
    setNow(Date.now());
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= sentAt + RESEND_WAIT_SECONDS * 1000) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [sentAt]);
  const clearTurnstileToken = useCallback(() => setTurnstileToken(null), []);
  const turnstileRef = useRef({
    token: null as string | null,
    captureUrl: turnstileConfig.captureUrl,
    reset: () => {},
  });
  useEffect(() => {
    turnstileRef.current = {
      token: turnstileToken,
      captureUrl: turnstileConfig.captureUrl,
      reset: () => {
        setTurnstileToken(null);
        setTurnstileKey((key) => key + 1);
      },
    };
  }, [turnstileToken, turnstileConfig]);

  const actions = useMemo<BuilderActions>(() => {
    const update = (fn: (s: BuilderState) => BuilderState) => store.set(fn);
    const setInputs = (patch: Partial<BuilderInputs>) => {
      update((s) => ({ ...s, inputs: { ...s.inputs, ...patch } }));
      clearTimeout(builtTimer.current);
      builtTimer.current = setTimeout(() => {
        const inputs = store.get().inputs;
        pushPbEvent("prompt_built", {
          ...promptParams(inputs),
          has_brand: inputs.brand.trim() !== "",
          has_audience: inputs.audience.trim() !== "",
        });
      }, BUILT_DEBOUNCE_MS);
    };
    const changed = (field: string, value?: string) =>
      pushPbEvent("input_changed", value === undefined ? { field } : { field, value });

    return {
      setGoal(goal) {
        setInputs({ goal, promptId: templatesInCategory(goal)[0].id });
        changed("goal", goal);
      },
      setPlatform(platform) {
        setInputs({ platform });
        changed("platform", platform);
      },
      setBusinessType(value) {
        const businessType = isBusinessType(value) ? value : "";
        setInputs({ businessType });
        update((s) => ({
          ...s,
          feedback: {
            ...s.feedback,
            fieldErrors: withoutError(s.feedback.fieldErrors, "businessType"),
          },
        }));
        changed("business_type", businessType);
      },
      setText(field, value) {
        const patch: Partial<BuilderInputs> = {};
        patch[field] = value;
        setInputs(patch);
      },
      setSlot(key, value) {
        const slots = { ...store.get().inputs.slots, [key]: value };
        setInputs({ slots });
      },
      commitText(field) {
        const inputs = store.get().inputs;
        const value =
          field === "tone" || field === "pillars" ? inputs[field] : slotValueOf(inputs, field);
        const trimmed = value.trim();
        if ((committed.current[field] ?? "") === trimmed) return;
        committed.current[field] = trimmed;
        pushPbEvent("input_changed", { field, filled: trimmed !== "" });
      },
      pickPrompt(id) {
        const template = getTemplate(id);
        if (!template) return;
        setInputs({ goal: template.category, promptId: template.id });
        changed("prompt", template.id);
      },
      tryAnother() {
        const inputs = store.get().inputs;
        const list = templatesInCategory(inputs.goal);
        const index = list.findIndex((t) => t.id === currentTemplate(inputs).id);
        const next = list[(index + 1) % list.length];
        setInputs({ promptId: next.id });
        changed("prompt", next.id);
      },
      toggleMore() {
        update((s) => ({ ...s, showMore: !s.showMore }));
      },
      showBusinessType() {
        update((s) => (s.showMore ? s : { ...s, showMore: true }));
        window.setTimeout(() => document.getElementById("pb-biz")?.focus(), 0);
      },
      generate() {
        const out = document.getElementById("pb-out");
        const reduced = prefersReducedMotion();
        document
          .getElementById("pb-result")
          ?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
        if (!out) return;
        out.focus({ preventScroll: true });
        if (reduced) return;
        // Restarts the pulse on a repeat press. React does not render this attribute.
        out.removeAttribute("data-pulse");
        void out.offsetWidth;
        out.setAttribute("data-pulse", "");
        out.onanimationend = () => out.removeAttribute("data-pulse");
      },
      copy(surface: CopySurface) {
        const inputs = store.get().inputs;
        const text = fillTemplate(currentTemplate(inputs), fillInputsFor(inputs));
        void copyText(text).then((ok) => {
          clearTimeout(copiedTimer.current);
          setCopyStatus(ok ? "copied" : "failed");
          if (ok) {
            pushPbEvent("prompt_copied", { ...promptParams(inputs), surface });
            copiedTimer.current = setTimeout(() => setCopyStatus("idle"), COPIED_RESET_MS);
          }
        });
      },
      openGate() {
        update((s) => ({ ...s, gateOpen: true }));
        pushPbEvent("gate_opened", promptParams(store.get().inputs));
      },
      closeGate() {
        update((s) => ({ ...s, gateOpen: false }));
      },
      setGateField(field, value) {
        const errorKey: CaptureField = field;
        update((s) => ({
          ...s,
          gate: { ...s.gate, [field]: value },
          feedback: { ...s.feedback, fieldErrors: withoutError(s.feedback.fieldErrors, errorKey) },
        }));
      },
      setNewsletter(checked) {
        update((s) => ({ ...s, gate: { ...s.gate, newsletter: checked } }));
      },
      checkEmail() {
        update((s) => {
          if (s.gate.email.trim() === "") {
            return {
              ...s,
              feedback: {
                ...s.feedback,
                nudge: false,
                fieldErrors: withoutError(s.feedback.fieldErrors, "email"),
              },
            };
          }
          const check = checkEmailField(s.gate.email);
          const fieldErrors = withoutError(s.feedback.fieldErrors, "email");
          if (check.error) fieldErrors.email = check.error;
          return { ...s, feedback: { ...s.feedback, nudge: check.nudge, fieldErrors } };
        });
      },
      submitGate() {
        void submit(false);
      },
      editEmail() {
        update((s) => ({
          ...s,
          feedback: { ...s.feedback, sentTo: null, sentAt: null, resent: false, formError: null },
        }));
        window.setTimeout(() => document.getElementById("g-email")?.focus(), 0);
      },
      resend() {
        void submit(true);
      },
    };

    // A resend posts the same answers again: the Worker issues another link and keeps the
    // earlier ones working.
    async function submit(isResend: boolean): Promise<void> {
      const state = store.get();
      if (state.feedback.submitting) return;
      const checked = checkGate(state);
      if (Object.keys(checked.fieldErrors).length > 0) {
        update((s) => ({
          ...s,
          feedback: { ...s.feedback, ...checked, formError: null, sentTo: null, sentAt: null },
        }));
        focusFirstError(checked.fieldErrors);
        return;
      }
      update((s) => ({
        ...s,
        feedback: {
          ...s.feedback,
          submitting: true,
          fieldErrors: {},
          formError: null,
          nudge: checked.nudge,
        },
      }));

      const fail = (patch: Partial<GateFeedback>) => {
        update((s) => ({ ...s, feedback: { ...s.feedback, ...patch, submitting: false } }));
        // The server spends the Turnstile token on any check past validation.
        turnstileRef.current.reset();
      };

      let res: Response;
      try {
        res = await fetch(turnstileRef.current.captureUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(captureBody(state, turnstileRef.current.token)),
        });
      } catch {
        fail({ formError: formErrorCopy("server_error") });
        return;
      }

      if (res.ok) {
        const data = (await res.json().catch(() => null)) as CaptureSuccess | null;
        if (data?.status !== "pending") {
          fail({ formError: formErrorCopy("server_error") });
          return;
        }
        update((s) => ({
          ...s,
          feedback: {
            ...s.feedback,
            submitting: false,
            sentTo: normaliseEmail(state.gate.email),
            sentAt: Date.now(),
            resent: isResend,
          },
        }));
        // The token is spent, so a resend needs a fresh challenge.
        turnstileRef.current.reset();
        if (!isResend) {
          window.setTimeout(() => document.getElementById("gate-sent-title")?.focus(), 0);
          // Lead fires on submit, as on the Traffic Snapshot Meta landing page.
          const emailHash = await sha256EmailHex(state.gate.email);
          pushPbEvent("lead", {
            lead_email_type: data.emailType,
            ...(emailHash ? { email_hash: emailHash } : {}),
          });
        }
        return;
      }

      const data = (await res.json().catch(() => null)) as CaptureFailure | null;
      const fields = data?.fields;
      if (fields && Object.keys(fields).length > 0) {
        const fieldErrors: Partial<Record<CaptureField, string>> = {};
        for (const field of CAPTURE_FIELD_ORDER) {
          const code = fields[field];
          if (code) fieldErrors[field] = fieldErrorCopy(field, code);
        }
        fail({ fieldErrors, nudge: false, sentTo: null, sentAt: null, resent: false });
        focusFirstError(fieldErrors);
        return;
      }
      const code = data?.error ?? (res.status === 429 ? "rate_limited" : "server_error");
      fail({ formError: formErrorCopy(code) });
    }
  }, [store]);

  useEffect(() => {
    pushPbEvent("view", {
      page_type: seed.page,
      goal: seed.goal,
      ...(seed.hook ? { hook: seed.hook } : {}),
    });
  }, [seed]);

  useEffect(
    () => () => {
      clearTimeout(builtTimer.current);
      clearTimeout(copiedTimer.current);
    },
    [],
  );

  useEffect(() => {
    const opened = state.gateOpen && !wasGateOpen.current;
    const closed = !state.gateOpen && wasGateOpen.current;
    wasGateOpen.current = state.gateOpen;
    if (closed) document.getElementById("pb-gate-open")?.focus();
    if (!opened) return;
    document.getElementById("gate")?.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "nearest",
    });
    document.getElementById("g-email")?.focus({ preventScroll: true });
  }, [state.gateOpen]);

  useEffect(() => {
    setMounted(true);
    const result = document.getElementById("pb-result");
    const builder = document.getElementById("builder");
    const gate = builder?.querySelector(".pb-g");
    if (!result || !builder || !gate || !("IntersectionObserver" in window)) return;
    const seen = new Map<Element, boolean>([
      [result, true],
      [builder, true],
      [gate, true],
    ]);
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) seen.set(entry.target, entry.isIntersecting);
      setCopybarVisible(!seen.get(result) && !!seen.get(builder) && !seen.get(gate));
    });
    for (const target of seen.keys()) observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const resendIn =
    state.feedback.sentAt === null
      ? 0
      : Math.max(0, Math.ceil((state.feedback.sentAt + RESEND_WAIT_SECONDS * 1000 - now) / 1000));

  const turnstile = turnstileConfig.show ? (
    <Turnstile
      key={turnstileKey}
      siteKey={turnstileConfig.siteKey}
      onToken={setTurnstileToken}
      onExpired={clearTurnstileToken}
      onError={clearTurnstileToken}
    />
  ) : null;

  return (
    <>
      <BuilderView
        page={seed.page}
        state={state}
        actions={actions}
        copyStatus={copyStatus}
        turnstile={turnstile}
        gateReady={!turnstileConfig.show || turnstileToken !== null}
        resendIn={resendIn}
        enhanced={mounted}
        flush={flushSync}
      />
      {mounted
        ? createPortal(
            <CopyBar
              title={currentTemplate(state.inputs).title}
              visible={copybarVisible}
              copyStatus={copyStatus}
              onCopy={() => actions.copy("copybar")}
            />,
            document.body,
          )
        : null}
    </>
  );
}
